import { useEntities } from "@/contexts/entities/EntitiesContext";
import { handleInternalNavigationButtonClick } from "@/lib/analytics/navigation";
import { Competency, LearnerOutcome } from "@/lib/models";
import ArrowRight from "../icons/ArrowRight";
import ArrowLeft from "../icons/ArrowLeft";
import colors from "@/lib/static/colors";
import {
  ButtonSizes,
  ButtonTypes,
  IconTypes,
  StyledButton,
} from "@/components/StyledButton";

type CompetencyPrevNextButtonsProps = {
  competency?: Competency;
  changeCompetency: (competencyId: string) => Promise<void>;
  learnerOutcome?: LearnerOutcome;
};

export const CompetencyPrevNextButtons = ({
  competency,
  changeCompetency,
  learnerOutcome,
}: CompetencyPrevNextButtonsProps) => {
  const { competencies } = useEntities();

  if (!competency) {
    return null;
  }

  const activeCompetencyId = competency.id;

  const activeCompetencyIndex = competencies.findIndex((competency) => {
    return competency.id == activeCompetencyId;
  });

  const finalIndex = competencies.length - 1;

  const prevIndex =
    activeCompetencyIndex == 0 ? finalIndex : activeCompetencyIndex - 1;

  const nextIndex =
    activeCompetencyIndex == finalIndex ? 0 : activeCompetencyIndex + 1;

  const prevCompetency = competencies[prevIndex];
  const nextCompetency = competencies[nextIndex];

  const handleCompetencyChange = (
    direction: string,
    navigatingToCompetency: string,
  ) => {
    direction === "previous"
      ? changeCompetency(prevCompetency.id)
      : changeCompetency(nextCompetency.id);

    const trackEventProps = {
      buttonName: direction,
      competency: competency?.name,
      learnerOutcome: learnerOutcome?.name,
      navigatingToCompetencyName: navigatingToCompetency,
    };

    handleInternalNavigationButtonClick(trackEventProps);
    const scrollToOptions = { top: 0, behavior: "smooth" };
    window.scrollTo(scrollToOptions as ScrollToOptions);
  };

  return (
    <div className="mt-10 flex justify-between medium:mt-12">
      <StyledButton
        buttonType={ButtonTypes.Secondary}
        size={ButtonSizes.Large}
        iconType={IconTypes.Left}
        onClick={() => handleCompetencyChange("previous", prevCompetency?.name)}
      >
        <ArrowLeft fill={colors["BG-60"]} />
        Previous
        <span className="hidden small:inline">: {prevCompetency?.name}</span>
      </StyledButton>

      <StyledButton
        buttonType={ButtonTypes.Secondary}
        size={ButtonSizes.Large}
        iconType={IconTypes.Right}
        onClick={() => handleCompetencyChange("next", nextCompetency?.name)}
      >
        Next
        <span className="hidden small:inline">: {nextCompetency?.name}</span>
        <ArrowRight fill={colors["BG-60"]} />
      </StyledButton>
    </div>
  );
};
